import { Modal } from "bootstrap";
import Cropper from "cropperjs";

interface AvatarManagerTexts {
    cropImage: string;
    confirm: string;
    cancel: string;
    invalidFileExtension: string;
    unexpectedError: string;
}

class AvatarManager {
    private readonly trigger: HTMLElement;
    private readonly endpoint: string;
    private readonly aspectRatio: number;
    private readonly allowedFileExtensions: string[];
    private readonly texts: AvatarManagerTexts;
    private readonly fileInput: HTMLInputElement;

    private modalElement: HTMLElement | null = null;
    private modal: Modal | null = null;
    private cropperInstance: Cropper | null = null;

    constructor(trigger: HTMLElement) {
        this.trigger = trigger;
        this.endpoint = trigger.dataset.avatarEndpoint ?? "";
        this.aspectRatio = trigger.dataset.avatarAspectRatio ? Number(trigger.dataset.avatarAspectRatio) : 1;
        this.allowedFileExtensions = (trigger.dataset.avatarFileExtensions ?? ".jpg,.jpeg,.png")
            .split(",")
            .map(extension => extension.trim().toLowerCase());
        this.texts = {
            cropImage: trigger.dataset.avatarTextCropImage ?? "Crop image",
            confirm: trigger.dataset.avatarTextConfirm ?? "Confirm",
            cancel: trigger.dataset.avatarTextCancel ?? "Cancel",
            invalidFileExtension: trigger.dataset.avatarTextInvalidExtension ?? "Invalid file type",
            unexpectedError: trigger.dataset.avatarTextError ?? "Something went wrong"
        };

        this.fileInput = document.createElement("input");
        this.fileInput.type = "file";
        this.fileInput.accept = this.allowedFileExtensions.join(",");
        this.fileInput.hidden = true;
        this.fileInput.addEventListener("change", () => this.onFileSelected());
        document.body.appendChild(this.fileInput);

        this.trigger.addEventListener("click", event => {
            event.preventDefault();
            this.fileInput.value = "";
            this.fileInput.click();
        });
    }

    private onFileSelected(): void {
        const file = this.fileInput.files?.[0];
        if (!file) {
            return;
        }

        const extension = `.${file.name.split(".").pop()?.toLowerCase() ?? ""}`;
        if (!this.allowedFileExtensions.includes(extension)) {
            window.alert(this.texts.invalidFileExtension);
            return;
        }

        const reader = new FileReader();
        reader.onload = () => this.openCropModal(reader.result as string);
        reader.readAsDataURL(file);
    }

    private openCropModal(imageSrc: string): void {
        this.modalElement = this.buildModalElement(imageSrc);
        document.body.appendChild(this.modalElement);

        const image = this.modalElement.querySelector<HTMLImageElement>("[data-role='crop-image']")!;
        const confirmButton = this.modalElement.querySelector<HTMLButtonElement>("[data-role='confirm']")!;
        confirmButton.addEventListener("click", () => void this.confirmCrop());

        this.modalElement.addEventListener("shown.bs.modal", () => {
            this.cropperInstance = new Cropper(image, {
                template: `
                    <cropper-canvas class="mtx-avatar-crop-canvas" scale-step="0.1">
                      <cropper-image initial-center-size="contain" scalable translatable></cropper-image>
                      <cropper-shade hidden></cropper-shade>
                      <cropper-handle action="select" plain></cropper-handle>
                      <cropper-selection initial-coverage="1" movable resizable aspect-ratio="${this.aspectRatio}">
                        <cropper-grid role="grid" bordered covered></cropper-grid>
                        <cropper-crosshair centered></cropper-crosshair>
                        <cropper-handle action="move" theme-color="rgba(255, 255, 255, 0.35)"></cropper-handle>
                        <cropper-handle action="n-resize"></cropper-handle>
                        <cropper-handle action="e-resize"></cropper-handle>
                        <cropper-handle action="s-resize"></cropper-handle>
                        <cropper-handle action="w-resize"></cropper-handle>
                        <cropper-handle action="ne-resize"></cropper-handle>
                        <cropper-handle action="nw-resize"></cropper-handle>
                        <cropper-handle action="se-resize"></cropper-handle>
                        <cropper-handle action="sw-resize"></cropper-handle>
                      </cropper-selection>
                    </cropper-canvas>
                `
            });
        });

        this.modalElement.addEventListener("hidden.bs.modal", () => this.destroyModal());

        this.modal = new Modal(this.modalElement);
        this.modal.show();
    }

    private buildModalElement(imageSrc: string): HTMLElement {
        const wrapper = document.createElement("div");
        wrapper.className = "modal fade";
        wrapper.tabIndex = -1;
        wrapper.setAttribute("aria-hidden", "true");
        wrapper.innerHTML = `
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">${this.texts.cropImage}</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="${this.texts.cancel}"></button>
                    </div>
                    <div class="modal-body mtx-avatar-crop-modal-body">
                        <img data-role="crop-image" src="${imageSrc}" class="mtx-hidden" />
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-outline-secondary px-4" data-bs-dismiss="modal">${this.texts.cancel}</button>
                        <button type="button" class="btn btn-outline-primary px-4" data-role="confirm">${this.texts.confirm}</button>
                    </div>
                </div>
            </div>
        `;

        return wrapper;
    }

    private async confirmCrop(): Promise<void> {
        const selection = this.cropperInstance?.getCropperSelection();
        if (!selection) {
            return;
        }

        const confirmButton = this.modalElement?.querySelector<HTMLButtonElement>("[data-role='confirm']");
        confirmButton?.setAttribute("disabled", "disabled");

        try {
            const canvas = await selection.$toCanvas({ width: 512, height: Math.round(512 / this.aspectRatio) });
            const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, "image/jpeg", 0.92));
            if (!blob) {
                return;
            }

            await this.upload(blob);
            this.modal?.hide();
            window.location.reload();
        } catch {
            window.alert(this.texts.unexpectedError);
        } finally {
            confirmButton?.removeAttribute("disabled");
        }
    }

    private async upload(blob: Blob): Promise<void> {
        const antiForgeryToken = document.querySelector<HTMLInputElement>("input[name='__RequestVerificationToken']")?.value ?? "";

        const formData = new FormData();
        formData.append("file", blob, "avatar.jpg");
        formData.append("__RequestVerificationToken", antiForgeryToken);

        const response = await fetch(this.endpoint, {
            method: "POST",
            body: formData,
            credentials: "same-origin"
        });

        if (!response.ok) {
            throw new Error(this.texts.unexpectedError);
        }
    }

    private destroyModal(): void {
        this.cropperInstance?.destroy();
        this.cropperInstance = null;

        this.modal?.dispose();
        this.modal = null;

        this.modalElement?.remove();
        this.modalElement = null;
    }
}

export function attachAvatarManager(button: HTMLElement): void {
    new AvatarManager(button);
}